import ManageTemplate from '../../Manage'
import * as S from './styles'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { colors } from '@monorepo/theme'
import { ISelectItem, Resource } from '@monorepo/interfaces'
import { useCallback, useState, useEffect } from 'react'
import { Prompt, useHistory, useParams } from 'react-router-dom'
import { ManageCard } from '../../../organisms'
import { Autocomplete, TextField } from '@mui/material'
import { DropzoneArea } from 'material-ui-dropzone'
import axios from 'axios'
import { useKeycloak } from '@react-keycloak/web'
import { http, ChannelHelper, KeycloakHelper, UserHelper } from '@monorepo/infra'
import CreatableSelect from 'react-select/creatable'

export enum ManageResourcesEditingMode {
  CREATING = 0,
  EDITING,
}

const channels: ISelectItem[] = ChannelHelper.getChannelSelectItems()

const LoadingStatus = {
  INITIAL: 'INITIAL',
  FETCHING: 'FETCHING',
  FETCHING_SUCCESS: 'FETCHING_SUCCESS',
  FETCHING_ERROR: 'FETCHING_ERROR',
  SAVING: 'SAVING',
  SAVING_SUCCESS: 'SAVING_SUCCESS',
  SAVING_ERROR: 'SAVING_ERROR',
  UPLOADING: 'UPLOADING',
  UPLOADING_SUCCESS: 'UPLOADING_SUCCESS',
  UPLOADING_ERROR: 'UPLOADING_ERROR',
}

export interface ManageResourcesProps {
  editingMode: ManageResourcesEditingMode
}

type ResourceType = Omit<
  Resource,
  'createdBy' | 'createdOn' | 'updatedBy' | 'updatedOn'
>

const initialValues = {
  title: '',
  description: '',
  document: '',
  status: 1,
  channels: [],
  categories: [],
  callToAction: '',
  // Held as a 'yyyy-MM-dd' string for the date input; converted to null
  // (no expiration) when empty on save.
  expirationDate: '',
}

// The API returns dates as ISO strings; the date input needs 'yyyy-MM-dd'.
const toDateInputValue = (value?: Date | string | null): string => {
  if (!value) {
    return ''
  }

  const date = new Date(value)

  return isNaN(date.getTime()) ? '' : format(date, 'yyyy-MM-dd')
}

const ManageResources: React.FC<ManageResourcesProps> = ({ editingMode }) => {
  const [loadingStatus, setLoadingStatus] = useState<string>(
    LoadingStatus.INITIAL
  )
  const [resource, setResource] = useState<ResourceType>(initialValues)
  const [categoriesOptions, setCategoriesOptions] = useState<
    { value: string; label: string }[]
  >([])
  // Controls the delete-confirmation modal. A React modal (not window.confirm)
  // so the tab isn't frozen while it's open — window.confirm blocks the event
  // loop, which stops Keycloak's background token refresh and makes a delayed
  // confirmation fire the request with an expired token (a 401).
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { keycloak } = useKeycloak()
  const user = KeycloakHelper.getTokenParsed(keycloak)
  // Permanent delete is for the internal admin team (Super Admins + DIRECTV
  // Admins, per AK guidance), mirroring the backend gate on
  // DELETE /resource/:id/permanent. Deliberately NOT dealerAdmin — those are
  // the external dealer principals. `user` is the current effective token
  // (during impersonation it's the dealer's token), so this correctly hides
  // the button while impersonating. Only shown when editing an existing
  // resource (nothing to delete while creating).
  const canPermanentlyDelete =
    editingMode === ManageResourcesEditingMode.EDITING &&
    (UserHelper.isSuperAdmin(user?.groups) ||
      UserHelper.isDirectvAdmin(user?.groups))
  const ResourceSchema = Yup.object().shape({
    title: Yup.string().required(),
    description: Yup.string().optional(),
    document: Yup.string().required(),
    status: Yup.number().required(),
    channels: Yup.array().of(Yup.string()).optional(),
    categories: Yup.array()
      .of(Yup.string())
      .min(1, 'At least one category is required'),
    // Required per AK guidance: the admin decides what the card link says
    callToAction: Yup.string().trim().required(),
    expirationDate: Yup.string().optional(),
  })
  const history = useHistory()
  const { id: resourceId } = useParams<{ id: string }>()

  const { errors, touched, handleBlur, ...formik } = useFormik({
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit: (values, { validateForm }) => {
      validateForm().then(() => {
        handleSubmit(values)
      })
    },
    validationSchema: ResourceSchema,
    initialValues: resource,
  })

  const handleGoBack = useCallback(() => {
    history.push('/admin/resources')
  }, [history])

  // The form holds expirationDate as a 'yyyy-MM-dd' string; send null when
  // empty (= no expiration, and clears an existing one on edit).
  const serializeValues = (values: ResourceType) => ({
    ...values,
    callToAction: values.callToAction?.trim(),
    expirationDate: values.expirationDate ? values.expirationDate : null,
  })

  const handleCreateResource = useCallback(
    (values: ResourceType) => {
      setLoadingStatus(LoadingStatus.SAVING)

      http
        .post({ url: `/resource`, data: serializeValues(values) })
        .then((response) => {
          const product = response?.data as ResourceType
          history.push(`/admin/resources/edit/${product?._id}`)
        })
        .catch((error) => {
          setLoadingStatus(LoadingStatus.SAVING_ERROR)
          throw error
        })
    },
    [history]
  )

  const handleEditResource = useCallback(
    (values: ResourceType) => {
      setLoadingStatus(LoadingStatus.SAVING)

      http
        .patch({ url: `/resource/${resourceId}`, data: serializeValues(values) })
        .then(() => {
          setLoadingStatus(LoadingStatus.SAVING_SUCCESS)
          formik.resetForm({ values })
        })
        .catch(() => {
          setLoadingStatus(LoadingStatus.SAVING_ERROR)
        })
    },
    [resourceId, formik]
  )

  const handleSubmit = useCallback(
    (values: ResourceType) => {
      if (editingMode === ManageResourcesEditingMode.CREATING) {
        handleCreateResource(values)
        return
      }

      if (editingMode === ManageResourcesEditingMode.EDITING) {
        handleEditResource(values)
        return
      }
    },
    [editingMode, handleCreateResource, handleEditResource]
  )

  const handleUploadFiles = (file: File, fileType?: string) => {
    if (file) {
      setLoadingStatus(LoadingStatus.UPLOADING)
      axios.create({
        baseURL: process.env.REACT_APP_REST_BASE_URL,
      })
      const url = `${process.env.REACT_APP_MEDIA_URL}`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('isInDesign', 'false')
      formData.append('tags', '')
      formData.append('email', user?.email)
      formData.append('mediaType', fileType || '')
      const config = {
        headers: {
          'content-type': 'multipart/form-data',
          Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
        },
      }
      axios
        .post(url, formData, config)
        .then((success) => {
          formik.setFieldValue('document', success?.data.path)
          setLoadingStatus(LoadingStatus.UPLOADING_SUCCESS)
        })
        .catch((error) => {
          setLoadingStatus(LoadingStatus.UPLOADING_ERROR)
        })
    }
  }

  useEffect(() => {
    const isEditing =
      resourceId && editingMode === ManageResourcesEditingMode.EDITING
    const isInitializing = loadingStatus === LoadingStatus.INITIAL
    const shouldSearchResource = isEditing && isInitializing
    if (!shouldSearchResource) return

    setLoadingStatus(LoadingStatus.FETCHING)

    http
      .get({ url: `/resource/${resourceId}` })
      .then((response) => {
        const resource = response?.data as ResourceType
        setResource({
          ...resource,
          callToAction: resource.callToAction ?? '',
          expirationDate: toDateInputValue(resource.expirationDate),
        })
        setLoadingStatus(LoadingStatus.FETCHING_SUCCESS)
      })
      .catch(() => {
        setLoadingStatus(LoadingStatus.FETCHING_ERROR)
      })
  }, [editingMode, resourceId, formik, loadingStatus])

  const handleDiscardChanges = useCallback(() => {
    formik.setValues(resource)
  }, [formik, resource])

  // Hard delete via DELETE /resource/:id/permanent — actually removes the
  // document (the Status dropdown's "Archived"/"Inactive" only hides it).
  // There is no undo, hence the confirmation modal.
  const confirmDeleteResource = useCallback(async () => {
    setIsDeleting(true)

    try {
      // Force a fresh access token before sending. Keycloak tokens live only
      // ~5 min; updateToken() refreshes if it expires within 60s. This is what
      // guarantees the request uses a valid ("right") token even if the modal
      // sat open for a while — the earlier 401 came from a stale token.
      await keycloak.updateToken(60)

      await http.delete({ url: `/resource/${resourceId}/permanent` })

      // Clear the dirty flag so the unsaved-changes Prompt doesn't block
      // leaving, then hard-navigate to the list. A full reload guarantees the
      // list/preview show fresh data — SWR would otherwise serve the cached
      // (pre-delete) array and the deleted item would linger until a manual
      // refresh (which is exactly the "still visible" bug we're fixing).
      formik.resetForm()
      window.location.href = '/admin/resources'
    } catch (error) {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setLoadingStatus(LoadingStatus.SAVING_ERROR)
      window.alert(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any)?.response?.data?.message ||
          'Failed to delete the resource. Please try again.'
      )
    }
  }, [resourceId, formik, keycloak])

  useEffect(() => {
    http
      .get({ url: '/resource/category' })
      .then((response) => {
        if (Array.isArray(response?.data)) {
          setCategoriesOptions(
            response?.data.map((item) => ({ value: item, label: item }))
          )
        }
      })
      .catch((error) => {
        console.log(error)
      })
  }, [])

  return (
    <ManageTemplate
      backText="Back to Resources"
      titleText={`${
        editingMode === ManageResourcesEditingMode.CREATING
          ? 'Create'
          : 'Manage'
      } Resource`}
      // onGoBack={handleGoBack}
      onGoBack={handleGoBack}
      headerRightSideChildren={
        <S.ButtonsContainer>
          {canPermanentlyDelete && (
            <S.FormButton
              backgroundColor={colors.red}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </S.FormButton>
          )}
          {formik.dirty && (
            <>
              <S.FormButton onClick={handleDiscardChanges}>
                Discard
              </S.FormButton>
              {formik.isValid && (
                <>
                  <S.FormButton
                    backgroundColor={colors.blue}
                    onClick={() => handleSubmit(formik.values)}
                  >
                    Save
                  </S.FormButton>
                </>
              )}
            </>
          )}
        </S.ButtonsContainer>
      }
    >
      <S.Wrapper>
        <S.ColumnsContainer>
          <Prompt
            when={!!formik.dirty}
            message={
              'You have made changes to this resource. If you leave, any unsaved changes will be lost. Are you sure?'
            }
          />
          <S.LeftColumn>
            <div className="two-thirds">
              <ManageCard title="Title">
                <S.TextInput
                  id="title"
                  name="title"
                  label="Title"
                  value={formik.values.title}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue('title', value)
                  }
                  onBlur={handleBlur}
                  invalid={!!errors.title && touched.title}
                  invalidMessage={errors.title}
                />
              </ManageCard>
              <ManageCard title="Description">
                <S.TextInput
                  id="description"
                  name="description"
                  label="Description"
                  value={formik.values.description}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue('description', value)
                  }
                  onBlur={handleBlur}
                  invalid={!!errors.description && touched.description}
                  invalidMessage={errors.description}
                />
              </ManageCard>
              <ManageCard title="Call to Action">
                <S.TextInput
                  id="callToAction"
                  name="callToAction"
                  label='Link text on the card, e.g. "Watch video"'
                  value={formik.values.callToAction}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue('callToAction', value)
                  }
                  onBlur={handleBlur}
                  invalid={!!errors.callToAction && touched.callToAction}
                  invalidMessage={errors.callToAction}
                />
              </ManageCard>
              <ManageCard title="Document">
                <DropzoneArea
                  // acceptedFiles={['image/jpeg', 'image/png', 'image/bmp']}
                  maxFileSize={100000000}
                  filesLimit={1}
                  onChange={(files) => {
                    handleUploadFiles(files[0], 'thumb')
                  }}
                ></DropzoneArea>
              </ManageCard>
            </div>
          </S.LeftColumn>
          <S.RightColumn>
            <div className="one-third">
              <ManageCard title="Status">
                <S.TextInput
                  as="select"
                  name="status"
                  // Normalize display: any non-active status (legacy Inactive=2
                  // or Archived=3) selects the single "Expired" option so a
                  // legacy status-3 item doesn't render a blank dropdown.
                  value={formik.values.status === 1 ? 1 : 2}
                  onChange={(e: { target: { value: string } }) => {
                    formik.setFieldValue('status', parseInt(e.target.value))
                  }}
                >
                  <option value="1">Active</option>
                  {/* Inactive + Archived merged into "Expired" (AK). Selecting
                      Expired writes status 2; the backend stamps expiredAt and
                      starts the 90-day auto-delete clock. Legacy status 3
                      still renders as Expired in the list. */}
                  <option value="2">Expired</option>
                </S.TextInput>
                {touched.status && errors.status && <div>{errors.status}</div>}
              </ManageCard>
              <ManageCard title="Expiration Date (optional)">
                <S.TextInput
                  id="expirationDate"
                  name="expirationDate"
                  type="date"
                  label=""
                  value={formik.values.expirationDate as string}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue('expirationDate', value)
                  }
                  onBlur={handleBlur}
                />
              </ManageCard>
              <ManageCard title="Channels">
                <Autocomplete
                  multiple
                  id="channels-outlined"
                  options={channels}
                  value={channels.filter((channel) =>
                    formik.values.channels?.includes(channel.id)
                  )}
                  getOptionLabel={(option: ISelectItem) => option.value}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="channels"
                      name="Channels"
                      label="Channels"
                    />
                  )}
                  onBlur={handleBlur}
                  onChange={(event, values) => {
                    formik.setFieldValue(
                      'channels',
                      values.map((value) => {
                        return value.id
                      })
                    )
                  }}
                />
              </ManageCard>
              <ManageCard title="Categories">
                <CreatableSelect
                  name="categories"
                  placeholder="DIRECTV, AT&T"
                  isMulti
                  options={categoriesOptions}
                  components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator: () => null,
                  }}
                  value={
                    Array.isArray(formik.values.categories)
                      ? formik.values.categories.map((category) => ({
                          value: category,
                          label: category,
                        }))
                      : []
                  }
                  onChange={(newValue) =>
                    formik.setFieldValue(
                      'categories',
                      newValue.map(({ value }) => value)
                    )
                  }
                  onBlur={() => formik.setFieldTouched('categories', true)}
                  styles={S.MultiSelectStyle}
                />
                {touched.categories && errors.categories && (
                  <S.FieldError>{errors.categories}</S.FieldError>
                )}
              </ManageCard>
            </div>
          </S.RightColumn>
        </S.ColumnsContainer>
      </S.Wrapper>

      {showDeleteConfirm && (
        <S.ConfirmOverlay
          onClick={() => !isDeleting && setShowDeleteConfirm(false)}
        >
          {/* stopPropagation so clicking inside the box doesn't dismiss it */}
          <S.ConfirmBox onClick={(e) => e.stopPropagation()}>
            <S.ConfirmTitle>Delete resource?</S.ConfirmTitle>
            <S.ConfirmMessage>
              Are you sure you want to delete{' '}
              <strong>{resource.title || 'this resource'}</strong>? This
              permanently removes it and cannot be undone.
            </S.ConfirmMessage>
            <S.ConfirmButtons>
              <S.FormButton
                color={colors.black}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </S.FormButton>
              <S.FormButton
                backgroundColor={colors.red}
                onClick={confirmDeleteResource}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Yes, delete'}
              </S.FormButton>
            </S.ConfirmButtons>
          </S.ConfirmBox>
        </S.ConfirmOverlay>
      )}
    </ManageTemplate>
  )
}

export default ManageResources
